import React, { FC, useCallback, useState } from 'react';

import { useRouter } from 'expo-router';
import { FlatList, FlatListProps } from 'react-native';

import type { IHistoricalOrder } from '@rbi-ctg/menu';
import DrawerPage from 'components/layout/drawer-page';
import { RefreshButton } from 'components/refresh-button';
import { RemoveUnavailableItemsModal } from 'components/remove-unavailable-items-modal';
import { useReorderableOrders } from 'hooks/use-reorderable-orders';
import { UserOrdersDefaultLimit } from 'hooks/use-user-orders';
import LoyaltyLoader from 'pages/loyalty/loyalty-loader/loyalty-loader';
import { t } from 'state/intl/translations';
import { useOrderContext } from 'state/order';
import { routes } from 'utils/routing';

import Order from './order';
import OrdersEmpty from './orders-empty';
import { OfferDisclaimer, OrdersEmptyWrapper } from './styled';

function historicalOrderKeyExtractor(item: IHistoricalOrder, index: number) {
  return item?.rbiOrderId || index + '_order';
}

export const AccountOrders: FC = () => {
  const { unavailableCartEntries } = useOrderContext();
  const { isLoading, orders, refetch } = useReorderableOrders(
    { limit: UserOrdersDefaultLimit },
    'no-cache'
  );

  const router = useRouter();

  const onRefreshPress = useCallback(() => {
    refetch();
  }, [refetch]);
  const [showOrderUnavailableModal, setShowOrderUnavailableModal] = useState(false);
  const handleUnavailableModalDismiss = useCallback(() => {
    setShowOrderUnavailableModal(false);
    router.replace(routes.cart);
  }, [router]);

  const refreshButton = <RefreshButton onPress={onRefreshPress}>{t('refresh')}</RefreshButton>;

  const hasOrders = !!orders.length;

  const renderHistoricalOrder = useCallback<
    NonNullable<FlatListProps<IHistoricalOrder>['renderItem']>
  >(({ item }) => <Order order={item} />, []);

  // this is needed to immediately show the modal if we have unavailable items, the component
  // it's duplicated bc of the modal controller issue
  if (unavailableCartEntries.length > 0) {
    return (
      <RemoveUnavailableItemsModal
        onConfirmCb={handleUnavailableModalDismiss}
        unavailableCartEntries={unavailableCartEntries}
      />
    );
  }

  function getContent() {
    if (isLoading) {
      return <LoyaltyLoader />;
    }

    if (hasOrders) {
      return (
        <FlatList
          testID="inner-layout"
          ListHeaderComponent={
            <>
              <OfferDisclaimer>{t('recentOrdersDisclaimer')}</OfferDisclaimer>
              {refreshButton}
            </>
          }
          keyExtractor={historicalOrderKeyExtractor}
          data={orders}
          renderItem={renderHistoricalOrder}
        />
      );
    }

    return (
      <OrdersEmptyWrapper>
        <OrdersEmpty>{refreshButton}</OrdersEmpty>
      </OrdersEmptyWrapper>
    );
  }

  return (
    <DrawerPage headerText={t('recentOrders')}>
      {getContent()}

      {showOrderUnavailableModal ? (
        <RemoveUnavailableItemsModal
          onConfirmCb={handleUnavailableModalDismiss}
          unavailableCartEntries={unavailableCartEntries}
        />
      ) : null}
    </DrawerPage>
  );
};
